import ISearchParams from '@shared/models/ISearchParams';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlertsStore } from './state/AlertsContainer';
import { useOrganizationStore } from './state/OrganizationContainer';
import useCurrentOrgDetailsFetching from './useCurrentOrgDetailsFetching';
import useScrollToElement from './useScrollToElement';
import { DEBOUNCE_MS } from '@shared/constants/Debounce';

export default function useCurrentOrgMemberSearch() {
    const { t } = useTranslation();
    const { currentOrg } = useCurrentOrgDetailsFetching();
    const organizationMembers = useOrganizationStore((state) => state.organizationMembers);
    const isLoading = useOrganizationStore((state) => state.loadingData.organizationMembers);
    const orgMembersSearchContentId = 'org-members-search';
    const defaultTake = 51;
    const displayLimit = defaultTake - 1;
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedQuery, setDebouncedQuery] = useState<ISearchParams>({
        searchText: '',
        skip: 0,
        take: defaultTake,
    });
    const members = useMemo(() => {
        if (!currentOrg) return null;
        return organizationMembers.find((org) => org.organizationId === currentOrg.id)?.members ?? [];
    }, [currentOrg, organizationMembers]);

    useScrollToElement(orgMembersSearchContentId, [debouncedQuery.skip]);

    function handleSearchQueryChange(value: string) {
        setSearchQuery(value);
    }

    const showPrevious = useMemo(() => debouncedQuery.skip && debouncedQuery.skip > 0, [debouncedQuery.skip]);
    const showNext = useMemo(() => organizationMembers.length >= displayLimit, [organizationMembers]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const newQuery = {
                searchText: searchQuery,
                skip: 0,
                take: defaultTake,
            };
            if (newQuery.searchText !== debouncedQuery.searchText) {
                setDebouncedQuery(newQuery);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    const handleNext = () => {
        const count = organizationMembers.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: displayLimit + (prev.skip ?? 0),
        }));
    };

    const handlePrevious = () => {
        const count = organizationMembers.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: Math.max(0, (prev.skip ?? 0) - displayLimit),
        }));
    };

    useEffect(() => {
        const trySearch = async () => {
            if (useOrganizationStore.getState().loadingData.organizationMembers) return;
            if (!currentOrg) return;
            try {
                useOrganizationStore.getState().handleLoadingDataChange('organizationMembers', true);
                await useOrganizationStore.getState().loadOrganizationMembers(currentOrg.id, debouncedQuery);
            } catch (e) {
                useAlertsStore.getState().showError(t('common:organization.member.loadError'));
            } finally {
                useOrganizationStore.getState().handleLoadingDataChange('organizationMembers', false);
            }
        };
        trySearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery]);

    return {
        results: members,
        orgMembersSearchContentId,
        handleSearchQueryChange,
        searchQuery,
        showPrevious,
        showNext,
        handleNext,
        handlePrevious,
        isLoading,
    };
}
